import { React, withRouter, serviceClient, dtos, observer, shared, observable, ui, Link, selectFilter, _, splitIntoWords, uuid, moveArray, removeArray, makeObservable, action, runInAction, cache } from "../common";
import ValueEditor from "../flows/value-editor";
import PageTitle from "../page-title";
import TagsEditor from "../tags-editor";
import AutoComplete from "../autocomplete";
import Sessions from "../system/sessions";
import Notifications from "../system/notifications";
import CustomersIndex from ".";
import CustomersList from "./customers-list";

interface Props {
    id: string;
    visible: boolean;
    onClosed: () => void;
    onSaved: (alert: dtos.AlertInfo) => void;
}

@observer
export default class AlertDetails extends React.Component<Props> {
    @observable name = "";
    @observable id = "";
    @observable editName = "";
    @observable editTrigger: dtos.AlertTriggers;
    @observable editThreshold = 0;
    @observable editWindowHours = 0;
    @observable editNotificationEmailAddresses = "";
    @observable editCustomerSelection = dtos.AlertCustomerSelection.AllCustomers;
    @observable editCustomerIds: string[] = [];
    @observable loading = true;
    @observable busy = false;
    @observable tab = "general";

    @observable pagination: any = { position: "bottom", total: 0 };
    @observable sort: any = {};
    @observable filter: any = {};
    @observable searching = false;
    @observable pageSize = 20;

    @observable alert: dtos.AlertInfo = null;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.refresh(this.props.id);
    }

    componentDidUpdate(prevProps) {
        var newId = this.props.id;
        if (newId != prevProps.id) {
            this.refresh(newId);
            this.tab = "general";
        }
    }

    async refresh(id) {
        let alert = (await serviceClient.get(new dtos.ListAlerts({ specificIds: [id] }))).items[0];
        let ss = await cache.getSystemSettings();
        this.id = alert.id;
        this.name = alert.name;
        this.editName = alert.name;
        this.editTrigger = alert.trigger;
        this.editThreshold = alert.threshold;
        this.editWindowHours = alert.windowHours;
        this.editNotificationEmailAddresses = alert.notificationEmailAddresses;
        this.editCustomerSelection = alert.customerSelection;
        this.editCustomerIds = alert.customerIds;
        this.alert = alert;
        this.loading = false;
    }

    async patch(ev: React.FormEvent) {
        ev.preventDefault();
        ev.stopPropagation();

        let updated = await serviceClient.patch(new dtos.PatchAlert({
            alertId: this.id,
            name: this.editName,
            windowHours: this.editWindowHours,
            threshold: this.editThreshold,
            trigger: this.editTrigger,
            notificationEmailAddresses: this.editNotificationEmailAddresses,
            customerSelection: this.editCustomerSelection,
            customerIds: this.editCustomerIds
        }));

        ui.message.success("Alert saved");

        this.name = this.editName;
        this.alert = updated;
        this.props.onSaved(updated);
        this.props.onClosed();
    }

    async deleteAlert() {
        await serviceClient.delete(new dtos.DeleteAlert({ alertId: this.id }));
        ui.message.success("Alert deleted");
        window.location.hash = "/integrations";
    }

    render() {
        let { visible, onClosed } = this.props;

        return (
            <ui.Modal visible={visible} title="Edit Alert" onCancel={onClosed} onOk={(ev) => this.patch(ev)} bodyStyle={{ paddingTop: 0 }}>
                <div>
                    {this.loading && <div style={{ textAlign: "center", paddingTop: 100 }}><ui.Spin size="default" /></div>}
                    {!this.loading && <>
                        <ui.Tabs>
                            <ui.Tabs.TabPane tab="General" key="general">
                                <ui.Form layout="vertical" onSubmitCapture={(ev) => this.patch(ev)}>
                                    <ui.Form.Item label="Name">
                                        <ui.Input value={this.editName} onChange={v => this.editName = v.target.value} />
                                    </ui.Form.Item>
                                    <ui.Form.Item label="Trigger">
                                        <ui.Select value={this.editTrigger} onChange={v => this.editTrigger = v} style={{ width: 300 }}>
                                            {Object.keys(dtos.AlertTriggers).map(k => <ui.Select.Option value={k} key={k}>{splitIntoWords(k)}</ui.Select.Option>)}
                                        </ui.Select>
                                        <p><small>Alerts are triggered based the total for an individual customer, for example a trigger of Call Spend with Threshold 100 and Window 8 means that if any customer's call spend within the past 8 hours exceeds $100, the alert will be triggered for that customer.</small></p>
                                    </ui.Form.Item>
                                    <ui.Form.Item label="Threshold">
                                        <ui.Input type="number" value={this.editThreshold} onChange={ev => this.editThreshold = parseFloat(ev.target.value) || 0} />
                                        <p><small>The threshold determines the amount past which the alert is triggered. For example if the threshold is set to 100, a call spend of 100.01 within the window interval would trigger the alert</small></p>
                                    </ui.Form.Item>
                                    <ui.Form.Item label="Window (hours)">
                                        <ui.Input type="number" value={this.editWindowHours} onChange={ev => this.editWindowHours = parseFloat(ev.target.value) || 0} />
                                        <p><small>The window determines the time frame over which the alert is calculated. For example a window of 1.5 means within the past 1.5 hours</small></p>
                                    </ui.Form.Item>
                                </ui.Form>
                            </ui.Tabs.TabPane>
                            <ui.Tabs.TabPane tab="Customers" key="customers">
                                <ui.Form layout="vertical" onSubmitCapture={(ev) => this.patch(ev)}>
                                    <ui.Form.Item label="Customer Selection" style={{marginBottom: 0}}>
                                        <ui.Select value={this.editCustomerSelection} onChange={ev => this.editCustomerSelection = ev as dtos.AlertCustomerSelection}>
                                            <ui.Select.Option value="AllCustomers">(All Customers)</ui.Select.Option>
                                            <ui.Select.Option value="ExcludeCustomers">Exclude Specific Customers</ui.Select.Option>
                                            <ui.Select.Option value="IncludeCustomers">Include Specific Customers</ui.Select.Option>
                                        </ui.Select>
                                    </ui.Form.Item>
                                    {(this.editCustomerSelection === dtos.AlertCustomerSelection.ExcludeCustomers || this.editCustomerSelection === dtos.AlertCustomerSelection.IncludeCustomers) && <div>
                                        <CustomersList customerIds={this.editCustomerIds} onChanged={ids => this.editCustomerIds = ids} />
                                        <p style={{ margin: 0 }}><small>To alert for all customers, select <strong>All Customers</strong></small></p>
                                        <p style={{ margin: 0 }}><small>To alert for all customers except for certain customers, select <strong>Exclude Specific Customers</strong></small></p>
                                        <p style={{ margin: 0 }}><small>To only alert for certain customers, select <strong>Include Specific Customers</strong></small></p>
                                    </div>}
                                </ui.Form>
                            </ui.Tabs.TabPane>
                            <ui.Tabs.TabPane tab="Notifications" key="notifications">
                                <ui.Form.Item label="Notify Email Addresses (one per line)">
                                    <ui.Input.TextArea value={this.editNotificationEmailAddresses} onChange={ev => this.editNotificationEmailAddresses = ev.target.value} placeholder="One per line" />
                                    <p><small>Alerts are also saved to the system log and can be found by searching for the alert's name</small></p>
                                </ui.Form.Item>
                            </ui.Tabs.TabPane>
                        </ui.Tabs>

                    </>}
                </div>
            </ui.Modal >
        );
    }
}
